import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import exploreImg from '../../../template/images/explore.png';
import Loader from '../../layout/lodaer';

ChartJS.register(ArcElement, Tooltip, Legend);

const LumpsumCalculator = () => {
    const [totalInvestment, setTotalInvestment] = useState(300000);
    const [expectedReturnRate, setExpectedReturnRate] = useState(12);
    const [timePeriod, setTimePeriod] = useState(10);
    const [investedAmount, setInvestedAmount] = useState(300000);
    const [estimatedReturns, setEstimatedReturns] = useState(2808477);
    const [totalValue, setTotalValue] = useState(5808477);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        if (initialLoad) {
            setTimeout(() => {
                setLoading(false);
                setInitialLoad(false);
            }, 2000);
        } else {
            calculateLumpsum();
        }
    }, [totalInvestment, expectedReturnRate, timePeriod]);

    useEffect(() => {
        if (!loading) {
            const sliders = document.querySelectorAll('input[type="range"]');
            sliders.forEach(slider => updateSliderColor({ target: slider }));
        }
    }, [loading]);

    const calculateLumpsum = () => {
        const rate = expectedReturnRate / 100;
        const invested = totalInvestment;
        const total = invested * Math.pow((1 + rate), timePeriod);
        const returns = total - invested;

        setInvestedAmount(Math.round(invested));
        setEstimatedReturns(Math.round(returns));
        setTotalValue(Math.round(total));
    };

    const updateSliderColor = (event) => {
        const target = event.target;
        const min = target.min;
        const max = target.max;
        const val = target.value;
        const percentage = (val - min) * 100 / (max - min);
        target.style.background = `linear-gradient(to right, #1b6098 0%, #1b6098 ${percentage}%, #ddd ${percentage}%, #ddd 100%)`;
    };

    const formatIndianNumber = (num) => {
        return num.toLocaleString('en-IN');
    };

    const data = {
        labels: ['Invested amount', 'Est. returns'],
        datasets: [
            {
                data: [investedAmount, estimatedReturns],
                backgroundColor: ['#46d8bf', '#1b6098'],
                borderWidth: 0,
                hoverBackgroundColor: ['#06fdd4', '#154d7f'],
                hoverBorderWidth: 0,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: true,
        cutout: '60%',
        plugins: {
            legend: {
                display: true,
                position: 'top',
                labels: {
                    boxWidth: 25,
                    padding: 10,
                    font: {
                        size: 14
                    }
                }
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed !== null) {
                            label += '₹' + formatIndianNumber(context.parsed);
                        }
                        return label;
                    }
                }
            },
            hover: {
                mode: 'nearest',
                intersect: true,
            },
        },
    };

    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <div className="lumpsum-calculator">
                    <div className="main-content">
                        <h2 className='heading'>Lumpsum Calculator</h2>
                        <div className="main-container">
                            <div className="calculator-box">
                                <div className="toggle-buttons">
                                    <button onClick={() => navigate("/SipCalculator")}>SIP</button>
                                    <button className="active">Lumpsum</button>
                                </div>
                                <div className="calculator-content">
                                    <div className="input-section">
                                        <div className="input-group">
                                            <div className="label-group">
                                                <label className='installment-types'>Total investment</label>
                                                <div className="input-container">
                                                    <span>₹</span>
                                                    <input
                                                        type="text"
                                                        value={formatIndianNumber(totalInvestment)}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/[^0-9]/g, '');
                                                            setTotalInvestment(Number(value));
                                                        }}
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                    />
                                                </div>
                                            </div>
                                            <input
                                                type="range"
                                                min="1000"
                                                max="10000000"
                                                value={totalInvestment}
                                                onChange={(e) => {
                                                    setTotalInvestment(Number(e.target.value));
                                                    updateSliderColor(e);
                                                }}
                                                onInput={updateSliderColor}
                                            />
                                        </div>
                                        <div className="input-group">
                                            <div className="label-group">
                                                <label className='installment-types'>Expected return rate (p.a)</label>
                                                <div className="input-container">
                                                    <input
                                                        type="text"
                                                        value={expectedReturnRate}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/[^0-9]/g, '');
                                                            setExpectedReturnRate(Number(value));
                                                        }}
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                    />
                                                    <span>%</span>
                                                </div>
                                            </div>
                                            <input
                                                type="range"
                                                min="1"
                                                max="30"
                                                value={expectedReturnRate}
                                                onChange={(e) => {
                                                    setExpectedReturnRate(Number(e.target.value));
                                                    updateSliderColor(e);
                                                }}
                                                onInput={updateSliderColor}
                                            />
                                        </div>
                                        <div className="input-group">
                                            <div className="label-group">
                                                <label className='installment-types'>Time period</label>
                                                <div className="input-container">
                                                    <input
                                                        type="text"
                                                        value={timePeriod}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/[^0-9]/g, '');
                                                            setTimePeriod(Number(value));
                                                        }}
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                    />
                                                    <span>Yr</span>
                                                </div>
                                            </div>
                                            <input
                                                type="range"
                                                min="1"
                                                max="30"
                                                value={timePeriod}
                                                onChange={(e) => {
                                                    setTimePeriod(Number(e.target.value));
                                                    updateSliderColor(e);
                                                }}
                                                onInput={updateSliderColor}
                                            />
                                        </div>
                                    </div>
                                    <div className="result-section">
                                        <div className="chart-container">
                                            <div className="chart">
                                                <Doughnut data={data} options={options} />
                                            </div>
                                        </div>
                                        <div className="result-details">
                                            <div className="detail">
                                                <span className='detail-span'>Invested amount</span>
                                                <span>₹{formatIndianNumber(investedAmount)}</span>
                                            </div>
                                            <div className="detail">
                                                <span className='detail-span'>Est. returns</span>
                                                <span>₹{formatIndianNumber(estimatedReturns)}</span>
                                            </div>
                                            <div className="detail">
                                                <span className='detail-span'>Total value</span>
                                                <span>₹{formatIndianNumber(totalValue)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="sidebar">
                                <div className="explore-news">
                                    <img src={exploreImg} alt="" />
                                    <h6>Explore financial news</h6>
                                    <p>Stay updated with the latest market trends and financial insights.</p>
                                    {/* <button className="explore-btn">EXPLORE</button> */}
                                    <a
                    href="https://finlabsindia.org/blog/"
                    className="explore-btn"
                  >
                    EXPLORE
                  </a>
                
                                </div>
                                <div className="popular-calculators">
                                    <h3>Popular Calculators</h3>
                                    <ul>
                                        <li><a href="/SipCalculator">SIP Calculator</a></li>
                                        <li><a href="#">Lumpsum Calculator</a></li>
                                        <li><a href="/RiskAnalyser">Risk analyser</a></li>
                                        {/* <li><a href="#">Retirement calculator</a></li> */}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default LumpsumCalculator;